// components/UserDetailModal.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Divider,
  Grid,
  Paper,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../Api/ApiUrls";

const SectionTitle = ({ title }) => (
  <>
    <Divider sx={{ my: 2 }} />
    <Typography variant="h6" fontWeight="bold" gutterBottom>
      {title}
    </Typography>
  </>
);

const UserDetailModal = ({ open, onClose, userid }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  // Fetch user details when modal opens or userid changes
  useEffect(() => {
    if (open && userid) {
      fetchUserDetails();
    }
  }, [open, userid]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/users/${userid}/`);
      
      if (response.data) {
        setUser(response.data);
      } else {
        toast.error("Failed to fetch user details");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  };

  // Reset user data when modal closes
  const handleClose = () => {
    setUser(null);
    onClose();
  };

  // Get status color for chips
  const getStatusColor = (status) => {
    if (status === "Active" || status === "Approved") return "success";
    if (status === "Inactive" || status === "Rejected") return "error";
    if (status === "Pending") return "warning";
    return "default";
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ backgroundColor: "#1976d2", color: "white" }}>
        User Complete Details
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : user ? (
          <>
            {/* ---------------- Basic Info ---------------- */}
            <SectionTitle title="Basic Information" />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography><strong>Name:</strong> {user.first_name} {user.last_name}</Typography>
                <Typography><strong>Username:</strong> {user.username}</Typography>
                <Typography><strong>Email:</strong> {user.email}</Typography>
                <Typography><strong>Mobile:</strong> {user.mobile_no}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography><strong>User ID:</strong> {user.user_id}</Typography>
                <Typography><strong>Role:</strong> {user.role}</Typography>
                <Typography><strong>Created At:</strong> {formatDate(user.created_at)}</Typography>
                {/* <Typography><strong>Razorpay ID:</strong> {user.razor_user_id || "-"}</Typography> */}
              </Grid>
            </Grid>

            {/* ---------------- User Types ---------------- */}
            <SectionTitle title="Subscription User Types" />
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {user.distinct_user_types?.length > 0 ? (
                  user.distinct_user_types.map((type, index) => (
                    <Chip 
                      key={index} 
                      label={type} 
                      color="primary" 
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))
                ) : (
                  <Typography>No User Types Available</Typography>
                )}
              </Stack>
            </Box>

            {/* ---------------- Property Types ---------------- */}
            <SectionTitle title="Property Types" />
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {user.distinct_property_types?.length > 0 ? (
                  user.distinct_property_types.map((type, index) => (
                    <Chip 
                      key={index} 
                      label={type} 
                      color="secondary" 
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))
                ) : (
                  <Typography>No Property Types Available</Typography>
                )}
              </Stack>
            </Box>

            {/* ---------------- Subscription Plans ---------------- */}
            {/* <SectionTitle title={`Subscription Plans (${user.user_sub_plan?.length || 0})`} />
            {user.user_sub_plan?.length > 0 ? (
              user.user_sub_plan.map((plan) => (
                <Paper key={plan.sub_id} elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography><strong>Plan Name:</strong> {plan.plan_name}</Typography>
                      <Typography><strong>User Type:</strong> {plan.user_type}</Typography>
                      <Typography><strong>Plan Type:</strong> {plan.plan_type || "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Status:</strong>{" "}
                        <Chip 
                          label={plan.status ? "Active" : "Inactive"} 
                          color={plan.status ? "success" : "error"}
                          size="small"
                        />
                      </Typography>
                      <Typography><strong>Expires On:</strong> {formatDate(plan.expired_date)}</Typography>
                      <Typography><strong>Properties:</strong> {plan.no_of_properties_unlimited ? "Unlimited" : plan.no_of_properties || "-"}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))
            ) : (
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography>No Subscription Plans Available</Typography>
              </Paper>
            )} */}

            {/* ---------------- Posted Properties ---------------- */}
            <SectionTitle title={`Posted Properties (${user.user_properties?.length || 0})`} />
            {user.user_properties?.length > 0 ? (
              user.user_properties.map((property) => (
                <Paper key={property.property_id} elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Property ID:</strong> {property.property_id}</Typography>
                      <Typography><strong>Type:</strong> {property.type || "-"}</Typography>
                      <Typography><strong>Property Type:</strong> {property.property_type || "-"}</Typography>
                      {property.property_name && (
                        <Typography><strong>Name:</strong> {property.property_name}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography>
                        <strong>Admin Status:</strong>{" "}
                        <Chip 
                          label={property.Admin_status || "Pending"} 
                          color={getStatusColor(property.Admin_status)}
                          size="small"
                        />
                      </Typography>
                      <Typography><strong>Posted By:</strong> {property.posted_by || "-"}</Typography>
                      <Typography><strong>Created:</strong> {formatDate(property.created_at)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography><strong>Location:</strong> {property.location || "-"}</Typography>
                      {property.price && (
                        <Typography><strong>Price:</strong> ₹ {property.price.toLocaleString()}</Typography>
                      )}
                      {property.min_budget && property.max_budget && (
                        <Typography>
                          <strong>Budget Range:</strong> ₹ {property.min_budget.toLocaleString()} - ₹ {property.max_budget.toLocaleString()}
                        </Typography>
                      )}
                      {property.min_acres && property.max_acres && (
                        <Typography>
                          <strong>Area:</strong> {property.min_acres} - {property.max_acres} acres
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))
            ) : (
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography>No Properties Posted</Typography>
              </Paper>
            )}

            {/* ---------------- Login History ---------------- */}
            <SectionTitle title={`Login History (${user.log_history?.length || 0})`} />
            {user.log_history?.length > 0 ? (
              user.log_history
                .slice() // avoid mutating original
                .reverse() // latest first
                .map((log) => {
                  const isActive = !log.logout_time;
                  return (
                    <Paper key={log.log_id} elevation={2} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography>
                            <strong>Login Time:</strong>{" "}
                            {formatDate(log.login_time)}
                          </Typography>
                          <Typography>
                            <strong>Logout Time:</strong>{" "}
                            {log.logout_time ? formatDate(log.logout_time) : "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography>
                            <strong>Session Status:</strong>{" "}
                            <Chip 
                              label={isActive ? "Active" : "Completed"} 
                              color={isActive ? "success" : "default"}
                              size="small"
                            />
                          </Typography>
                          {isActive && (
                            <Typography color="success.main" variant="caption" sx={{ display: 'block', mt: 1 }}>
                              ● Currently logged in
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Log ID:</strong> {log.log_id} |{" "}
                            <strong>Created:</strong> {formatDate(log.created_at)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })
            ) : (
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography>No Login History Available</Typography>
              </Paper>
            )}

            {/* ---------------- Credits & Referral ---------------- */}
            {/* <SectionTitle title="Credits & Referrals" />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography><strong>Credit Points:</strong> {user.credit_points || 0}</Typography>
                <Typography><strong>Total Points Earned:</strong> {user.total_points_earned || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Total Referrals:</strong> {user.total_referred || 0}</Typography>
                <Typography><strong>Upgrade Plan:</strong> {user.upgrade_plan || 0}</Typography>
              </Grid>
            </Grid> */}
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <Typography>No user data available</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailModal;